import React, { ChangeEvent, FormEvent, useMemo, useRef, useState } from 'react';
import './EventoRegistro.css';
import eventoCeroVideo from '../assets/videos/evento.mp4';
import eventoUnoVideo from '../assets/videos/evento1.mp4';
import eventoDosVideo from '../assets/videos/evento2.mp4';
import eventoTresVideo from '../assets/videos/evento3.mp4';

const UVAS_BLANCAS = [
  'Torrontes',
  'Chardonnay',
  'Sauvignon Blanc',
  'Chenin',
  'Semillon',
];

const UVAS_TINTAS = [
  'Malbec',
  'Cabernet Sauvignon',
  'Cabernet Franc',
  'Pinot Noir',
  'Bonarda',
];

const UVA_OPTIONS = [
  ...UVAS_BLANCAS.map((uva) => ({ value: uva, label: `${uva} · Blanca` })),
  ...UVAS_TINTAS.map((uva) => ({ value: uva, label: `${uva} · Tinta` })),
];

const EVENTO_UPLOAD_URL = process.env.REACT_APP_EVENTO_UPLOAD_URL ?? '/.netlify/functions/evento-upload';
const FORM_ERROR_MESSAGE =
  'No se pudo enviar el formulario. Revisa la información o contactanos directo a nuestro WhatsApp para poder ayudarte con la inscripción';

type FormState = {
  nombreCompleto: string;
  edad: string;
  telefono: string;
  mail: string;
  uvasFavoritas: string[];
};

type FieldName = 'nombreCompleto' | 'edad' | 'telefono' | 'mail' | 'uvasFavoritas' | 'comprobante';

type FormErrors = Partial<Record<FieldName, string>>;

const INITIAL_FORM: FormState = {
  nombreCompleto: '',
  edad: '',
  telefono: '',
  mail: '',
  uvasFavoritas: [],
};

const validateForm = (form: FormState, comprobante: File | null): FormErrors => {
  const errors: FormErrors = {};

  if (!form.nombreCompleto.trim()) {
    errors.nombreCompleto = 'Completá tu nombre completo.';
  }

  if (!form.edad) {
    errors.edad = 'Completá tu edad.';
  } else if (Number(form.edad) < 18) {
    errors.edad = 'Este es un evento con bebidas alcohólicas, debés tener más de 18 para ingresar.';
  }

  if (!form.telefono.trim()) {
    errors.telefono = 'Completá tu teléfono.';
  }

  if (!form.mail.trim()) {
    errors.mail = 'Completá tu mail.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.mail.trim())) {
    errors.mail = 'Ingresá un mail válido.';
  }

  if (form.uvasFavoritas.length === 0) {
    errors.uvasFavoritas = 'Seleccioná al menos una uva favorita.';
  }

  if (!comprobante) {
    errors.comprobante = 'Subí el comprobante para completar la inscripción.';
  }

  return errors;
};

const EventoRegistro: React.FC = () => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [comprobante, setComprobante] = useState<File | null>(null);
  const [touchedFields, setTouchedFields] = useState<Partial<Record<FieldName, boolean>>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [estado, setEstado] = useState<'idle' | 'error' | 'success'>('idle');
  const [mensaje, setMensaje] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videos = [eventoCeroVideo, eventoUnoVideo, eventoDosVideo, eventoTresVideo];

  const resumenArchivo = useMemo(() => {
    if (!comprobante) {
      return 'Todavía no subiste ningún comprobante.';
    }

    const sizeInMb = (comprobante.size / (1024 * 1024)).toFixed(2);
    return `${comprobante.name} · ${sizeInMb} MB`;
  }, [comprobante]);

  const formErrors = validateForm(form, comprobante);
  const isFormReady = Object.keys(formErrors).length === 0;

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.playbackRate = 0.42;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => undefined);
    }
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setEstado('idle');
    setMensaje('');
    setTouchedFields((current) => ({ ...current, [name]: true }));
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleUvaCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    setEstado('idle');
    setMensaje('');
    setTouchedFields((current) => ({ ...current, uvasFavoritas: true }));
    setForm((current) => ({
      ...current,
      uvasFavoritas: checked
        ? [...current.uvasFavoritas, value]
        : current.uvasFavoritas.filter((uva) => uva !== value),
    }));
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    setComprobante(nextFile);
    setEstado('idle');
    setMensaje('');
    setTouchedFields((current) => ({ ...current, comprobante: true }));
  };

  const handleVideoEnded = () => {
    setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videos.length);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitAttempted(true);

    if (Object.keys(formErrors).length > 0) {
      setEstado('error');
      setMensaje(FORM_ERROR_MESSAGE);
      return;
    }

    if (!EVENTO_UPLOAD_URL) {
      setEstado('error');
      setMensaje(FORM_ERROR_MESSAGE);
      return;
    }

    const comprobanteFile = comprobante as File;
    const payload = new FormData();
    payload.append('nombreCompleto', form.nombreCompleto.trim());
    payload.append('edad', form.edad);
    payload.append('telefono', form.telefono.trim());
    payload.append('mail', form.mail.trim());
    payload.append('uvasFavoritas', JSON.stringify(form.uvasFavoritas));
    payload.append('comprobante', comprobanteFile);

    try {
      setSubmitting(true);
      setEstado('idle');
      setMensaje('');

      const response = await fetch(EVENTO_UPLOAD_URL, {
        method: 'POST',
        body: payload,
      });

      if (!response.ok) {
        let errorDetail = `HTTP ${response.status}`;
        try {
          const errorBody = await response.json();
          if (errorBody?.detail) {
            errorDetail = `${errorDetail} - ${errorBody.detail}`;
          } else if (errorBody?.error) {
            errorDetail = `${errorDetail} - ${errorBody.error}`;
          }
        } catch (parseError) {
          // Ignorado: si no hay JSON, mantenemos el status HTTP.
        }

        throw new Error(errorDetail);
      }

      setForm(INITIAL_FORM);
      setComprobante(null);
      setTouchedFields({});
      setSubmitAttempted(false);
      setEstado('success');
      setMensaje('Registro exitoso');
    } catch (error) {
      console.error('No se pudo enviar el registro del evento', error);
      setEstado('error');
      setMensaje(FORM_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="evento-registro-page">
      <div className="evento-registro-media">
        <video
          key={currentVideoIndex}
          ref={videoRef}
          className="evento-registro-video"
          autoPlay
          muted
          playsInline
          preload="metadata"
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleVideoEnded}
        >
          <source src={videos[currentVideoIndex]} type="video/mp4" />
        </video>
      </div>
      <div className="evento-registro-overlay" />

      <div className="evento-registro-shell">
        {estado === 'success' ? (
          <section className="evento-success-card" aria-live="polite">
            <h2>¡Registro exitoso!</h2>
            <p>
              ¡Muchas gracias por sumarte al club! Te enviaremos la confirmación de tu
              transferencia y toda la información del evento al telefono que nos brindaste,
              ¡Nos vemos pronto!
            </p>
          </section>
        ) : (
          <>
            <div className="evento-registro-hero">
              <section className="evento-registro-hero-card">
                <p className="evento-registro-kicker">JG Wine Club</p>
                <h1>Inscripción al Evento 26/6 - 20:30hs</h1>
                <div className="evento-registro-meta">
                  <div className="evento-registro-map-card">
                    <span className="evento-registro-map-label">Ubicación</span>
                    <div className="evento-registro-map-embed">
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3283.7109856395805!2d-58.3734931!3d-34.6114691!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x95bccba03a909543%3A0x75ac8688e5821b0!2sTERTULIAS%2C%20Palacio%20de%20vinos%20%7C%20Eventos%20Personalizados!5e0!3m2!1ses!2sar!4v1780677356378!5m2!1ses!2sar"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Ubicación del evento"
                        allowFullScreen
                      />
                    </div>
                    <strong>TERTULIAS, Palacio de vinos | Eventos Personalizados</strong>
                  </div>
                </div>
              </section>

              <section className="evento-registro-hero-card">
                <p className="evento-registro-subtitle">
                  ¿Estás preparado para formar parte del mejor club de vinos y vivir una noche única?
                </p>
                <p className="evento-registro-copy">
                  Vení a explorar nuevos sabores, conocer nuevas etiquetas y encontrarte con otros
                  fanáticos de la uva. Completá el formulario, hacé la transferencia y asegurá tu lugar
                  en esta experiencia inolvidable.
                </p>
              </section>
            </div>

            <form className="evento-registro-form" onSubmit={handleSubmit}>
              <div className="evento-form-card">
                <label className="evento-field">
                  <span>Nombre Completo</span>
                  <input
                    type="text"
                    name="nombreCompleto"
                    value={form.nombreCompleto}
                    onChange={handleChange}
                    autoComplete="name"
                  />
                  {(submitAttempted || touchedFields.nombreCompleto) && formErrors.nombreCompleto && (
                    <small className="evento-field-error">{formErrors.nombreCompleto}</small>
                  )}
                </label>
              </div>

              <div className="evento-form-card">
                <label className="evento-field">
                  <span>Edad</span>
                  <input
                    type="number"
                    name="edad"
                    min="18"
                    value={form.edad}
                    onChange={handleChange}
                  />
                  {(submitAttempted || touchedFields.edad) && formErrors.edad && (
                    <small className="evento-field-error">{formErrors.edad}</small>
                  )}
                </label>
              </div>

              <div className="evento-form-card">
                <label className="evento-field">
                  <span>Teléfono</span>
                  <input
                    type="tel"
                    name="telefono"
                    value={form.telefono}
                    onChange={handleChange}
                    autoComplete="tel"
                  />
                  {(submitAttempted || touchedFields.telefono) && formErrors.telefono && (
                    <small className="evento-field-error">{formErrors.telefono}</small>
                  )}
                </label>
              </div>

              <div className="evento-form-card">
                <label className="evento-field">
                  <span>Mail</span>
                  <input
                    type="email"
                    name="mail"
                    value={form.mail}
                    onChange={handleChange}
                    autoComplete="email"
                  />
                  {(submitAttempted || touchedFields.mail) && formErrors.mail && (
                    <small className="evento-field-error">{formErrors.mail}</small>
                  )}
                </label>
              </div>

              <div className="evento-form-card">
                <div className="evento-field">
                  <strong className="evento-field-label">¡Contanos cuáles son tus uvas favoritas!</strong>
                  <small className="evento-field-hint">(Podés elegir todas las que quieras)</small>
                  <div className="evento-uvas-grid" role="group" aria-label="Uvas favoritas">
                    {UVA_OPTIONS.map((option) => (
                      <label key={option.value} className="evento-uva-option">
                        <input
                          type="checkbox"
                          name="uvasFavoritas"
                          value={option.value}
                          checked={form.uvasFavoritas.includes(option.value)}
                          onChange={handleUvaCheckboxChange}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                  {(submitAttempted || touchedFields.uvasFavoritas) && formErrors.uvasFavoritas && (
                    <small className="evento-field-error">{formErrors.uvasFavoritas}</small>
                  )}
                </div>
              </div>

              <aside className="evento-transfer-card evento-form-card">
                <p className="evento-transfer-label">Datos para transferir</p>
                <h2>Transferencia bancaria</h2>
                <dl>
                  <div>
                    <dt>Alias</dt>
                    <dd>jg.evento</dd>
                  </div>
                  <div>
                    <dt>CVU</dt>
                    <dd>0000003100050208422302</dd>
                  </div>
                  <div>
                    <dt>Nombre</dt>
                    <dd>Fernando Ezequiel Pane</dd>
                  </div>
                </dl>
                <p className="evento-transfer-note">
                  Hacé la transferencia, subí el comprobante y enviá el formulario para reservar tu lugar.
                </p>
              </aside>

              <div className="evento-form-card">
                <label className="evento-field">
                  <span>Comprobante de transferencia</span>
                  <label className="evento-file-input-wrap">
                    <span className="evento-file-input-button">Elegir archivo</span>
                    <input
                      type="file"
                      name="comprobante"
                      accept=".jpg,.jpeg,.png,.pdf,.webp"
                      onChange={handleFileChange}
                    />
                  </label>
                  <small>{resumenArchivo}</small>
                  {(submitAttempted || touchedFields.comprobante) && formErrors.comprobante && (
                    <small className="evento-field-error">{formErrors.comprobante}</small>
                  )}
                </label>
              </div>

              <div className="evento-form-card evento-form-card--submit">
                <button
                  type="submit"
                  className="evento-submit-button"
                  disabled={submitting || !isFormReady}
                >
                  {submitting ? 'Enviando...' : 'Enviar registro'}
                </button>

                {mensaje && estado === 'error' && (
                  <p className="evento-form-message error">
                    {mensaje}
                  </p>
                )}
              </div>
            </form>
          </>
        )}
      </div>
    </section>
  );
};

export default EventoRegistro;
